import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, Mail, LifeBuoy, Send, MessageCircle } from "lucide-react";

const SUPPORT_EMAIL = "support@littlelearnerapp.com";

const FAQS = [
  {
    q: "How do I set up an account?",
    a: "You can start learning right away on the Free plan — no account needed. To save your subscription and use it on any device, open the Parent tab and create a parent account with your email and a password.",
  },
  {
    q: "What's the difference between Free and Premium?",
    a: "Free includes the ABC and Numbers sections plus two mini-games (Memory and Catch the Stars). Premium unlocks all 10 learning categories, 50+ activities, and every mini-game, along with Daily Adventures, badges and stickers.",
  },
  {
    q: "How do payments and subscriptions work?",
    a: "Go to the Pricing tab and choose Monthly (₹99/month) or Yearly (₹799/year). Payments are handled securely by Razorpay — you can pay with UPI, cards, net banking or wallets. Your plan stays active until the expiry date shown on the Parent page.",
  },
  {
    q: "How do I restore Premium on another device?",
    a: "Your Premium plan follows your email. On the new device, open the Parent tab, log in with the same email you used to pay, and tap Restore. Your plan and access will be reinstated on that device.",
  },
  {
    q: "I'm having trouble accessing content I paid for.",
    a: "First, make sure you're logged in on the Parent tab with the same email used for payment, then tap Restore. If a payment was interrupted but money was deducted, access usually unlocks automatically within a minute — if not, log in and tap Restore, or contact us below and we'll help.",
  },
];

function FaqItem({ item, open, onToggle, index }) {
  return (
    <div className="ll-card overflow-hidden" data-testid={`faq-item-${index}`}>
      <button onClick={onToggle} data-testid={`faq-toggle-${index}`}
        className="w-full flex items-center justify-between gap-4 p-5 text-left">
        <span className="font-display font-bold text-lg text-purple-900">{item.q}</span>
        <ChevronDown size={22} className={`shrink-0 text-purple-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 -mt-1 text-purple-700 font-semibold" data-testid={`faq-answer-${index}`}>{item.a}</div>}
    </div>
  );
}

export default function Help() {
  const [open, setOpen] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const body = `Name: ${form.name}%0AEmail: ${form.email}%0A%0A${encodeURIComponent(form.message)}`;
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(form.subject || "Little Learner support")}&body=${body}`;
    toast.success("Thanks! Your message is ready to send — we'll get back to you soon. 💜");
    setForm({ name: "", email: "", subject: "", message: "" });
  };

  return (
    <div className="ll-bg min-h-screen pb-28 md:pb-12">
      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-6">
        <div className="text-center pt-4 max-w-2xl mx-auto">
          <div className="text-purple-500 uppercase tracking-widest text-xs font-bold">We're here for you</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl text-purple-900 mt-1">Help &amp; Contact Us</h1>
          <p className="text-purple-700/70 font-semibold mt-3">Answers to common questions, and a quick way to reach us if you need a hand.</p>
        </div>

        {/* FAQ */}
        <section className="mt-10">
          <h2 className="font-display font-bold text-2xl text-purple-900">Frequently asked questions</h2>
          <div className="grid gap-3 mt-4">
            {FAQS.map((item, i) => (
              <FaqItem key={i} item={item} index={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
            ))}
          </div>
        </section>

        <div className="grid lg:grid-cols-2 gap-6 mt-10 items-start">
          {/* Contact form */}
          <section className="ll-card p-6 sm:p-7" data-testid="contact-form-card">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 text-white flex items-center justify-center"><MessageCircle size={22} /></div>
              <h2 className="font-display font-bold text-2xl text-purple-900">Contact us</h2>
            </div>
            <form onSubmit={submit} className="mt-5 space-y-3">
              <input data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Name" className="ll-input w-full" />
              <input data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="Email" className="ll-input w-full" />
              <input data-testid="contact-subject-input" required value={form.subject} onChange={set("subject")} placeholder="Subject" className="ll-input w-full" />
              <textarea data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="How can we help?" className="ll-input w-full resize-none" />
              <button data-testid="contact-send-button" type="submit" className="ll-btn ll-btn-primary w-full text-lg"><Send size={18} /> Send Message</button>
            </form>
          </section>

          {/* Need more help */}
          <section className="ll-card p-6 sm:p-7 bg-gradient-to-br from-violet-50 to-purple-50 border-purple-200" data-testid="need-help-card">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-yellow-300 text-purple-900 flex items-center justify-center"><LifeBuoy size={22} /></div>
              <h2 className="font-display font-bold text-2xl text-purple-900">Need more help?</h2>
            </div>
            <p className="text-purple-700 font-semibold mt-4">
              Still stuck or have a question that isn't answered above? Our friendly team is happy to help you and your little learner.
            </p>
            <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="support-email-link"
              className="ll-btn ll-btn-secondary mt-5 inline-flex"><Mail size={18} /> {SUPPORT_EMAIL}</a>
            <p className="text-purple-600/80 text-sm font-semibold mt-5">
              Tip: for subscription questions, log in on the <Link to="/parent" className="underline text-purple-700">Parent</Link> page first — many things (like restoring Premium) can be fixed there in a tap.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
